import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { LOOK_AHEAD_COUNT, useList } from "./hooks/useList";
import { useInfiniteLoader } from "./hooks/useInfiniteLoader";
import { SortableList } from "./SortableList";
import classNames from "classnames";
import { useHeight } from "./hooks/userHeight";
const renderNull = () => null;
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const ListContext = React.createContext(null);
export const List = React.forwardRef((props, ref) => {
    const context = React.useContext(ListContext);
    const contentRef = React.useRef(null);
    const { style, className, itemWidth, noContentRenderer = renderNull, infiniteLoad, itemRenderer, headerItemRenderer = itemRenderer, footerItemRenderer = itemRenderer, moveRows, getItemKey, isStickyFooterItem } = props;
    const { isRowLoaded = undefined, loadMoreRows = undefined, hasMoreRows = false, pageSize = 100, loadingRenderer = renderNull } = props.infiniteLoad ? props : {};
    if (process.env.NODE_ENV !== "development") {
        if (context?.pageHeader && props.pageHeader)
            throw new Error("Invalid properties");
        if (context?.pageFooter && props.pageFooter)
            throw new Error("Invalid properties");
    }
    const pageHeader = context?.pageHeader || props.pageHeader;
    const pageFooter = context?.pageFooter || props.pageFooter;
    const isStickyPageHeader = context?.isStickyPageHeader || props.isStickyPageHeader;
    const isStickyPageFooter = context?.isStickyPageFooter || props.isStickyPageFooter;
    const [activeRowIndex, setActiveRowIndex] = React.useState();
    const [pageHeaderHeight, pageHeaderRef] = useHeight();
    const [pageFooterHeight, pageFooterRef] = useHeight();
    const [containerElement, setContainerElement] = React.useState(null);
    const options = {
        pageHeaderHeight,
        pageFooterHeight,
        isStickyPageHeader,
        isStickyPageFooter,
        activeRowIndex,
        containerElement: containerElement
    };
    const { buildItemProps, rowVirtualizer, listRef, headerRows, bodyRows, footerRows } = useList(props, options);
    const headerItemCount = props.headerItemCount ?? 0;
    const footerItemCount = hasMoreRows && !isStickyFooterItem ? 0 : props.footerItemCount ?? 0;
    const itemCount = (hasMoreRows ? props.itemCount + LOOK_AHEAD_COUNT : props.itemCount) + headerItemCount + footerItemCount;
    // const itemCount = hasMoreRows ? itemCount2 + LOOK_AHEAD_COUNT : itemCount2;
    // const headerItemCount = Math.min(props.headerItemCount || 0, itemCount);
    // const footerItemCount = hasMoreRows && !isStickyFooterItem ? 0 : Math.min(props.footerItemCount || 0, itemCount - headerItemCount);
    React.useImperativeHandle(ref, () => ({
        ...listRef,
        contentRef,
    }), [listRef]);
    useInfiniteLoader({
        isRowLoaded, loadMoreRows,
        startIndex: (bodyRows[0]?.index ?? headerItemCount) - headerItemCount,
        stopIndex: (bodyRows.at(-1)?.index ?? headerItemCount) - headerItemCount,
        pageSize, hasMoreRows
    });
    const renderContents = () => {
        const isLoading = infiniteLoad && hasMoreRows && itemCount <= headerItemCount + footerItemCount + LOOK_AHEAD_COUNT;
        if (isLoading)
            return loadingRenderer();
        const renderListHeader = () => {
            if (headerItemCount === 0)
                return null;
            const fixedRowheight = headerRows.reduce((pv, cv) => pv + cv.size, 0);
            let stickyTop = isStickyPageHeader ? pageHeaderHeight : 0;
            stickyTop = stickyTop - 1; //HACK: Without this a small band of text appears right above the table header row
            return _jsx("div", { "data-id": "header-rows", style: { position: "sticky", top: stickyTop, left: 0, height: fixedRowheight, zIndex: 2 }, children: headerRows.map(virtualRow => headerItemRenderer(buildItemProps(virtualRow, "header"))) });
        };
        const renderListFooter = () => {
            if (footerItemCount === 0)
                return null;
            if (isStickyFooterItem) {
                const listFooterHeight = footerRows.reduce((pv, cv) => pv + cv.size, 0);
                const listFooterTop = containerElement ? containerElement.clientHeight - pageFooterHeight - listFooterHeight : 0;
                return _jsx("div", { "data-id": "footer-rows", style: { position: "sticky", top: listFooterTop, left: 0, height: listFooterHeight, zIndex: 2 }, children: footerRows.map(virtualRow => footerItemRenderer(buildItemProps(virtualRow, "footer"))) });
            }
            //If the footer is not sticky it will have the same properties as regular list item
            return footerRows.map(virtualRow => footerItemRenderer(buildItemProps(virtualRow, "content")));
        };
        return _jsxs(_Fragment, { children: [renderListHeader(), _jsx("div", { ref: contentRef, "data-id": "contents", style: { display: "contents" }, children: itemCount === 0 ? noContentRenderer() :
                        bodyRows.map(virtualRow => itemRenderer(buildItemProps(virtualRow, "content"))) }), renderListFooter()] });
    };
    const renderPageHeader = () => {
        if (!pageHeader)
            return null;
        const stickyHeaderStyle = isStickyPageHeader ? { position: "sticky", top: 0 } : {};
        return React.cloneElement(pageHeader, {
            ref: pageHeaderRef,
            ...pageHeader.props,
            style: { ...pageHeader.props.style, ...stickyHeaderStyle }
        });
    };
    const renderPageFooter = () => {
        if (!pageFooter)
            return null;
        const stickyFooterStyle = isStickyPageFooter ?
            { position: "sticky", top: containerElement ? containerElement.clientHeight - pageFooterHeight : 0 } :
            { position: "absolute", bottom: 0, width: itemWidth };
        return React.cloneElement(pageFooter, {
            ref: pageFooterRef,
            ...pageFooter.props,
            style: { ...pageFooter.props.style, ...stickyFooterStyle }
        });
    };
    const renderList = () => {
        const listStyle = { ...style, ...context?.style, overflow: "auto", height: "100%", outline: "none" };
        const contentHeight = rowVirtualizer.getTotalSize();
        return _jsx("div", { ref: setContainerElement, "data-id": "main-container", style: listStyle, className: classNames(className, context?.className), tabIndex: -1, children: _jsxs("div", { "data-id": "scroller", style: { height: contentHeight, minHeight: "100%", width: itemWidth, position: "relative" }, children: [renderPageHeader(), renderContents(), renderPageFooter()] }) });
    };
    const renderOverlayItem = () => {
        const activeRow = activeRowIndex !== undefined ? bodyRows.find(x => x.index === activeRowIndex) : undefined;
        if (!activeRow)
            return null;
        const overLayItemProps = buildItemProps(activeRow, "overlay");
        return _jsx("div", { children: itemRenderer(overLayItemProps) });
    };
    if (!moveRows || !getItemKey)
        return renderList();
    return _jsx(SortableList, { activeRowIndex: activeRowIndex, setActiveRowIndex: setActiveRowIndex, moveRows: (fromIndex, toIndex) => moveRows(fromIndex - headerItemCount, toIndex - headerItemCount), items: bodyRows, renderOverlayItem: renderOverlayItem, children: renderList() });
});
